import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, effect, inject, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Actions, ofType } from '@ngrx/effects';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { AbilityService } from 'ability';
import { ApplicationStateFacade } from 'mbs-ui-shared@facades/application.facade';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { BuildType } from 'mbs-ui-shared@models/BuildType.enum';
import { Build, ConnectFromBuild } from 'mbs-ui-shared@models/build';
import { BuildService } from 'mbs-ui-shared@services/build-service/build.service';
import { ApplicationStore } from 'mbs-ui-shared@store/application/application.store';
import * as AuthStoreActions from 'mbs-ui-shared@store/auth/actions';
import { EMPTY, forkJoin, noop, pipe } from 'rxjs';
import { exhaustMap, filter, switchMap, takeUntil, tap } from 'rxjs/operators';
import { SettingsType, ShowUpdatesAlertType } from './builds.types';
import {
  VersionsHashCodes,
  agentVersionsConfig,
  extractSettings,
  getInitialBuildList,
  getUpdateDots,
  getVersionsHashCodes,
  mergeBuildLists,
  noUpdateDots,
  sortGeneratedFirst
} from './builds.utils';

type BuildsState = SettingsType &
  ShowUpdatesAlertType & {
    builds: Build[];
    buildsInProgress: BuildType[];
    connectFromBuilds: ConnectFromBuild[];
    buildsLoading: boolean;
    updateDotsPending: boolean;
    refreshPending: boolean;
  };

const initialState: BuildsState = {
  isAutoUpdateEnabled: null,
  isBetaAvailable: null,
  isSandBoxAvailable: null,
  isAutoGenerateEnabled: null,
  isSettingsButtonVisible: null,
  builds: null,
  buildsInProgress: [],
  connectFromBuilds: [],
  buildsLoading: false,
  updateDotsPending: false,
  refreshPending: false,
  ...noUpdateDots
};

export const BuildsStore = signalStore(
  { providedIn: 'root' },
  withDevtools('BuildsStore'),
  withState<BuildsState>(initialState),

  withProps(() => ({
    logout$: inject(Actions, { optional: true })?.pipe(ofType(AuthStoreActions.logout)) ?? EMPTY
  })),

  withComputed((store) => ({
    settings: computed<SettingsType>(() => ({
      isAutoUpdateEnabled: store.isAutoUpdateEnabled(),
      isBetaAvailable: store.isBetaAvailable(),
      isSandBoxAvailable: store.isSandBoxAvailable(),
      isSettingsButtonVisible: store.isSettingsButtonVisible(),
      isAutoGenerateEnabled: store.isAutoGenerateEnabled()
    })),
    newVersionsAlerts: computed<ShowUpdatesAlertType>(() => ({
      showBackupUpdateDot: store.showBackupUpdateDot(),
      showRmmUpdateDot: store.showRmmUpdateDot(),
      showRAUpdateDot: store.showRAUpdateDot()
    })),
    isReady: computed(() => !!store.builds() && !store.updateDotsPending())
  })),

  withMethods((store) => {
    const buildService = inject(BuildService);
    const ability = inject(AbilityService);
    const auth = inject(AuthFacade);
    const uiStorage = inject(ApplicationStateFacade);

    const getNewVersions = () => buildService.getNewVersions(store.builds(), store.isSandBoxAvailable());

    const toHashCodes = (versions = getNewVersions()): VersionsHashCodes =>
      getVersionsHashCodes(versions, (items) => buildService.generateHashCode(items));

    const saveHashCode = (storageKey: keyof VersionsHashCodes, hashCode: string): void => {
      uiStorage.updateStorageSetting(storageKey, hashCode).subscribe({ error: noop });
    };

    const isRegularUser = (): boolean => {
      const user = auth.currentUserValue;

      return !!user && !user.IsSuperAdmin;
    };

    const loadInitial = rxMethod<void>(
      pipe(
        filter(() => !store.builds() && !store.buildsLoading()),
        tap(() => patchState(store, { buildsLoading: true })),
        exhaustMap(() =>
          forkJoin([buildService.getDownloadInfo(), buildService.getAll()]).pipe(
            takeUntil(store.logout$),
            tapResponse({
              next: ([availableBuilds, buildsInfo]) =>
                patchState(store, {
                  builds: sortGeneratedFirst(getInitialBuildList(availableBuilds, buildsInfo, ability.can('read', 'Rebranding'))),
                  ...extractSettings(buildsInfo),
                  connectFromBuilds: buildsInfo.connectFromBuilds,
                  buildsLoading: false,
                  updateDotsPending: isRegularUser()
                }),
              error: () => patchState(store, { buildsLoading: false })
            })
          )
        )
      )
    );

    const refresh = rxMethod<void>(
      pipe(
        tap(() => patchState(store, { refreshPending: false })),
        switchMap(() =>
          forkJoin([buildService.getAll(), buildService.getActiveBuildRequest()]).pipe(
            takeUntil(store.logout$),
            tapResponse({
              next: ([buildsInfo, activeRequests]) => {
                patchState(store, {
                  builds: mergeBuildLists(store.builds(), buildsInfo.builds),
                  ...extractSettings(buildsInfo),
                  buildsInProgress: activeRequests.map(({ requestedBuild }) => requestedBuild),
                  connectFromBuilds: buildsInfo.connectFromBuilds
                });

                if (!isRegularUser()) return;

                const hashCodes = toHashCodes();

                Object.values(agentVersionsConfig).forEach(({ storageKey }) => saveHashCode(storageKey, hashCodes[storageKey]));
              },
              error: noop
            })
          )
        )
      )
    );

    return {
      loadInitial,

      loadInProgress: rxMethod<void>(
        pipe(
          switchMap(() =>
            buildService.getActiveBuildRequest().pipe(
              takeUntil(store.logout$),
              tapResponse({
                next: (activeRequests) =>
                  patchState(store, { buildsInProgress: activeRequests.map(({ requestedBuild }) => requestedBuild) }),
                error: noop
              })
            )
          )
        )
      ),

      requestRefresh(): void {
        if (store.isReady()) return void refresh();

        patchState(store, { refreshPending: true });
        loadInitial();
      },

      addInProgress(buildTypes: BuildType[]): void {
        patchState(store, { buildsInProgress: [...store.buildsInProgress(), ...buildTypes] });
      },

      markVersionsSeen(agentType: string): void {
        const config = agentVersionsConfig[agentType];

        if (!config || !store.isReady()) return;

        patchState(store, { [config.dotKey]: false });
        saveHashCode(config.storageKey, toHashCodes()[config.storageKey]);
      },

      refresh,

      applyUpdateDots(storage: Record<string, unknown>): void {
        const versions = getNewVersions();

        patchState(store, { ...getUpdateDots(versions, toHashCodes(versions), storage), updateDotsPending: false });
      }
    };
  }),

  withHooks((store) => ({
    onInit() {
      const applicationStore = inject(ApplicationStore);

      effect(() => {
        if (store.updateDotsPending() && applicationStore.storageLoaded()) {
          untracked(() => store.applyUpdateDots(applicationStore.storage()));
        }
      });

      effect(() => {
        if (store.refreshPending() && store.isReady()) {
          untracked(() => store.refresh());
        }
      });

      store.logout$.pipe(takeUntilDestroyed()).subscribe(() => patchState(store, initialState));
    }
  }))
);

export type BuildsStoreType = InstanceType<typeof BuildsStore>;
